/**
 * Barcode-Erkennung aus dem Kamerabild.
 *
 * Zwei Wege, weil kein einzelner überall funktioniert:
 *
 * - **BarcodeDetector** ist in Chrome/Android eingebaut, schnell und kostet
 *   nichts im Bundle.
 * - **ZXing** wird nur dann nachgeladen, wenn BarcodeDetector fehlt. Vor
 *   allem auf iOS. Als eigener Chunk per dynamischem Import: sonst zahlte
 *   jedes Android-Gerät für einen Rückfall, den es nie braucht.
 *
 * Die beiden Wege sind nicht gleich gut. Der eingebaute Erkenner liest
 * Strichcodes deutlich zuverlässiger als ZXing in der Voreinstellung.
 * Deshalb bekommt ZXing die Formate und TRY_HARDER ausdrücklich mitgegeben:
 * ohne das prüft es je Bild nur wenige Zeilen und findet einen leicht
 * schrägen oder unscharfen Strichcode nicht. Auf dem iPhone las derselbe
 * Code nicht, der am Rechner sofort ging.
 *
 * Das Kartenformat ist bis heute unbekannt (O-3). Deshalb wird nach allen
 * gängigen 1D- und 2D-Formaten gleichzeitig gesucht; der Scanner muss nicht
 * wissen, was auf der Karte steht. Er ist ausdrücklich **additiv**. Die
 * Zifferneingabe deckt die Anforderung auch ohne ihn ab.
 */

export const SCAN_FORMATS = [
  'qr_code',
  'code_128',
  'code_39',
  'code_93',
  'ean_13',
  'ean_8',
  'itf',
  'pdf417',
  'data_matrix',
  'codabar',
] as const;

export interface Scanner {
  /** Liefert den zuletzt erkannten Rohwert oder null. */
  scan(source: CanvasImageSource): Promise<string | null>;
  readonly kind: 'native' | 'zxing';
}

/**
 * Unsere Formatnamen auf die von ZXing. Kein Grossbuchstaben-Umbau:
 * `pdf417` heisst dort `PDF_417`.
 */
const ZXING_NAME: Record<(typeof SCAN_FORMATS)[number], string> = {
  qr_code: 'QR_CODE',
  code_128: 'CODE_128',
  code_39: 'CODE_39',
  code_93: 'CODE_93',
  ean_13: 'EAN_13',
  ean_8: 'EAN_8',
  itf: 'ITF',
  pdf417: 'PDF_417',
  data_matrix: 'DATA_MATRIX',
  codabar: 'CODABAR',
};

interface DetectedBarcode {
  rawValue: string;
}

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}

type BarcodeDetectorCtor = new (options?: { formats?: string[] }) => BarcodeDetectorLike;

/** Nur die Formate anfordern, die das Gerät auch kennt. */
async function nativeScanner(ctor: BarcodeDetectorCtor): Promise<Scanner> {
  const supported = await (
    ctor as unknown as { getSupportedFormats?: () => Promise<string[]> }
  ).getSupportedFormats?.();

  const formats = supported
    ? SCAN_FORMATS.filter((f) => supported.includes(f))
    : [...SCAN_FORMATS];

  const detector = new ctor(formats.length > 0 ? { formats } : undefined);

  return {
    kind: 'native',
    async scan(source) {
      const found = await detector.detect(source);
      return found.length > 0 ? found[0].rawValue : null;
    },
  };
}

/**
 * Rückfall über ZXing. Der Import steht bewusst im Funktionsrumpf: so
 * entsteht ein eigener Chunk, der nur auf Geräten ohne BarcodeDetector
 * geladen wird.
 */
async function zxingScanner(): Promise<Scanner> {
  const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([
    import('@zxing/browser'),
    import('@zxing/library'),
  ]);

  const formate = SCAN_FORMATS.map(
    (name) => (BarcodeFormat as unknown as Record<string, number>)[ZXING_NAME[name]],
  ).filter((wert) => wert !== undefined);

  const hints = new Map<number, unknown>();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, formate);
  // Kostet Rechenzeit je Bild, findet dafuer auch schraege und unscharfe
  // Codes. Auf einem Telefon in der Hand ist beides der Normalfall.
  hints.set(DecodeHintType.TRY_HARDER, true);

  const reader = new BrowserMultiFormatReader(hints as never);
  // Eine Zeichenflaeche fuer alle Bilder. Vier neue Flaechen je Sekunde
  // beschaeftigten den Speicherbereiniger ohne Not.
  let canvas: HTMLCanvasElement | null = null;

  return {
    kind: 'zxing',
    async scan(source) {
      canvas = toCanvas(source, canvas);
      if (!canvas) return null;
      try {
        return reader.decodeFromCanvas(canvas).getText();
      } catch {
        // Kein Code im Bild. Das ist der Normalfall zwischen zwei Karten.
        return null;
      }
    },
  };
}

function toCanvas(
  source: CanvasImageSource,
  vorhanden: HTMLCanvasElement | null = null,
): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  const video = source as HTMLVideoElement;
  const width = video.videoWidth || 640;
  const height = video.videoHeight || 480;

  const canvas = vorhanden ?? document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0, width, height);
  return canvas;
}

export async function createScanner(
  global: { BarcodeDetector?: BarcodeDetectorCtor } = window as never,
): Promise<Scanner> {
  return global.BarcodeDetector
    ? nativeScanner(global.BarcodeDetector)
    : zxingScanner();
}
